import { Asset, Folder, FolderContent } from '@/api/generatedApi';
import { GET_ASSET_QUERY_KEY } from '@/hooks/api/assets/useGetAsset';
import { ASSET_STATE_COUNTS_QUERY_KEY } from '@/hooks/api/assets/useGetAssetStateCounts';
import { CROSS_TENANT_TEXT_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/textsearch/query';
import { CROSS_TENANT_VISUAL_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/visualSearch/query';
import { CROSS_TENANT_ASSETS_GEOMETRIC_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/assetsGeometricSearch/query';
import { CROSS_TENANT_ASSETS_PART_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/assetsPartSearch/query';
import { CROSS_TENANT_ASSETS_VISUAL_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/assetsVisualSearch/query';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { LIST_ASSETS_BY_STATE_QUERY_KEY } from '@/hooks/api/assets/useFetchAssetsByState';
import { GET_FOLDER_HIERARCHY_QUERY_KEY } from '@/hooks/api/folders/useFolderHierarchy';
import { useQueryClient } from '@tanstack/react-query';
import { BulkDeleteModal } from './BulkDeleteModal';
import { ContentTreeItem, sortContents, itemKey } from './DeleteFolderModal/DeletionContentsTree';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { Box } from '@mui/material';
import { useState } from 'react';

const isAsset = (content: FolderContent): content is Asset & { contentType: 'asset' } => {
  return content.contentType === 'asset';
};

export const BulkDeleteContentModal = <T extends FolderContent>({
  open,
  onClose,
  selectedContent,
  onDeleteComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedContent: T[];
  onDeleteComplete?: () => void;
}) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const handleDeleteComplete = () => {
    queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [CROSS_TENANT_TEXT_SEARCH_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [CROSS_TENANT_ASSETS_PART_SEARCH_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [CROSS_TENANT_ASSETS_GEOMETRIC_SEARCH_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [CROSS_TENANT_VISUAL_SEARCH_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [CROSS_TENANT_ASSETS_VISUAL_SEARCH_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY], exact: false });

    for (const asset of selectedContent.filter(content => isAsset(content)).map(content => content as Asset)) {
      queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY, { assetId: asset?.id ?? '' }] });
    }

    onDeleteComplete?.();
  };

  const sorted = sortContents(selectedContent as FolderContent[]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const folders = selectedContent.filter(
    (c): c is T & Folder & { contentType: 'folder' } => c.contentType === 'folder'
  );
  const selectedAssetCount = selectedContent.filter(c => isAsset(c)).length;
  const childAssetCount = folders.reduce((sum, f) => sum + f.assetsCount, 0);
  const childFolderCount = folders.reduce((sum, f) => sum + f.foldersCount, 0);
  const totalAssets = selectedAssetCount + childAssetCount;
  const totalFolders = childFolderCount;

  const buildDescription = (count: number): string => {
    const label = count === 1 ? 'item' : 'items';
    const parts: string[] = [];
    if (totalAssets > 0) {
      parts.push(`${totalAssets} ${totalAssets === 1 ? 'asset' : 'assets'}`);
    }
    if (totalFolders > 0) {
      parts.push(`${totalFolders} ${totalFolders === 1 ? 'subfolder' : 'subfolders'}`);
    }
    const including = parts.length > 0 ? `, including ${parts.join(' and ')}` : '';
    return `${count} ${label} will be permanently deleted${including}. This action cannot be undone.`;
  };

  return (
    <BulkDeleteModal
      open={open}
      onClose={onClose}
      selectedItems={selectedContent}
      onDeleteComplete={handleDeleteComplete}
      deleteItem={async content => {
        const tenantId = getTenantId();
        await (isAsset(content)
          ? api.assets.deleteAsset(tenantId, content.id)
          : api.folders.deleteFolder(tenantId, content.id));
      }}
      itemLabel={{ singular: 'item', plural: 'items' }}
      dataTestId={AssetsPageObjectModel.BulkDeleteModal}
      description={buildDescription}
    >
      <Box
        sx={{
          mt: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 250,
          overflowY: 'auto',
          p: 1,
        }}
      >
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
          sx={{
            '& .MuiTreeItem-content': {
              borderRadius: 1,
            },
          }}
        >
          {sorted.map(item => (
            <ContentTreeItem key={itemKey(item)} content={item} />
          ))}
        </SimpleTreeView>
      </Box>
    </BulkDeleteModal>
  );
};
